import { useEffect, useMemo, useState } from "react";
import { Edit3, Plus, Search, Trash2 } from "lucide-react";
import http from "../api/http";
import Modal from "../components/Modal";

const estadoInicial = () => ({ nombre: "", descripcion: "", precio: "" });
const moneda = (value) => Number(value || 0).toLocaleString("es-AR", {
  style: "currency",
  currency: "ARS",
});

export default function MaterialesProduccion() {
  const [materiales, setMateriales] = useState([]);
  const [formulario, setFormulario] = useState(estadoInicial);
  const [editando, setEditando] = useState(null);
  const [busqueda, setBusqueda] = useState("");
  const [modalAbierto, setModalAbierto] = useState(false);
  const [cargando, setCargando] = useState(false);
  const [mensaje, setMensaje] = useState("");
  const [error, setError] = useState("");

  const cargarMateriales = async () => {
    try {
      setCargando(true);
      setError("");
      const { data } = await http.get("/materiales-produccion");
      setMateriales(Array.isArray(data) ? data : []);
    } catch (err) {
      setError(err.response?.data?.error || "No se pudieron cargar los materiales.");
    } finally {
      setCargando(false);
    }
  };

  useEffect(() => {
    // Initial data load for this screen.
    cargarMateriales();
  }, []);

  const materialesFiltrados = useMemo(() => {
    const query = busqueda.trim().toLowerCase();
    if (!query) return materiales;
    return materiales.filter((material) =>
      `${material.nombre || ""} ${material.descripcion || ""}`.toLowerCase().includes(query),
    );
  }, [materiales, busqueda]);

  const abrirCrear = () => {
    setEditando(null);
    setFormulario(estadoInicial());
    setError("");
    setModalAbierto(true);
  };

  const abrirEditar = (material) => {
    setEditando(material);
    setFormulario({
      nombre: material.nombre || "",
      descripcion: material.descripcion || "",
      precio: material.precio ?? "",
    });
    setError("");
    setModalAbierto(true);
  };

  const cerrarModal = () => {
    setModalAbierto(false);
    setEditando(null);
    setFormulario(estadoInicial());
  };

  const guardar = async (event) => {
    event.preventDefault();
    if (!formulario.nombre.trim()) {
      setError("El nombre es obligatorio.");
      return;
    }
    try {
      setCargando(true);
      setError("");
      const payload = { ...formulario, precio: Number(formulario.precio || 0) };
      if (editando) {
        await http.put(`/materiales-produccion/${editando._id}`, payload);
        setMensaje("Material actualizado.");
      } else {
        await http.post("/materiales-produccion", payload);
        setMensaje("Material creado.");
      }
      cerrarModal();
      await cargarMateriales();
    } catch (err) {
      setError(err.response?.data?.error || "No se pudo guardar el material.");
    } finally {
      setCargando(false);
    }
  };

  const eliminar = async (material) => {
    if (!window.confirm(`Eliminar ${material.nombre}?`)) return;
    try {
      setCargando(true);
      setError("");
      await http.delete(`/materiales-produccion/${material._id}`);
      setMensaje("Material eliminado.");
      await cargarMateriales();
    } catch (err) {
      setError(err.response?.data?.error || "No se pudo eliminar el material.");
    } finally {
      setCargando(false);
    }
  };

  return (
    <div>
      <div className="page-header actions-only">
        <div className="header-actions">
          <button className="btn btn-primary" type="button" onClick={abrirCrear}>
            <Plus size={18} />Nuevo material
          </button>
        </div>
      </div>

      {mensaje && <div className="alert alert-ok">{mensaje}</div>}
      {error && !modalAbierto && <div className="alert alert-error">{error}</div>}

      <section className="panel">
        <div className="toolbar">
          <div>
            <h2>Materiales cargados</h2>
            <p>Total: {materialesFiltrados.length}</p>
          </div>
          <div className="search">
            <Search size={17} />
            <input
              value={busqueda}
              onChange={(event) => setBusqueda(event.target.value)}
              placeholder="Buscar por nombre o descripcion..."
            />
          </div>
        </div>
        <div className="table-wrap">
          <table>
            <thead>
              <tr>
                <th>Nombre</th>
                <th>Descripcion</th>
                <th>Precio unitario</th>
                <th>Ingresado</th>
                <th>Consumido</th>
                <th>Stock actual</th>
                <th>Acciones</th>
              </tr>
            </thead>
            <tbody>
              {cargando && materiales.length === 0 ? (
                <tr><td colSpan="7">Cargando...</td></tr>
              ) : materialesFiltrados.length === 0 ? (
                <tr><td colSpan="7">Sin materiales.</td></tr>
              ) : materialesFiltrados.map((material) => (
                <tr key={material._id}>
                  <td><strong>{material.nombre}</strong></td>
                  <td>{material.descripcion || "-"}</td>
                  <td>{moneda(material.precio)}</td>
                  <td>{Number(material.stockIngresado || 0).toLocaleString("es-AR")} unidades</td>
                  <td>{Number(material.stockConsumido || 0).toLocaleString("es-AR")} unidades</td>
                  <td><strong>{Number(material.stockActual || 0).toLocaleString("es-AR")} unidades</strong></td>
                  <td>
                    <div className="row-actions">
                      <button className="btn action-icon-button" type="button" title="Editar" aria-label="Editar" onClick={() => abrirEditar(material)}>
                        <Edit3 size={16} />
                      </button>
                      <button className="btn btn-danger action-icon-button" type="button" title="Eliminar" aria-label="Eliminar" onClick={() => eliminar(material)}>
                        <Trash2 size={16} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>

      <Modal
        abierto={modalAbierto}
        titulo={editando ? "Editar material" : "Nuevo material"}
        subtitulo="Datos y precio unitario del material."
        onClose={cerrarModal}
        footer={
          <>
            <button className="btn" type="button" onClick={cerrarModal}>Cancelar</button>
            <button className="btn btn-primary" form="material-produccion-form" type="submit" disabled={cargando}>Guardar</button>
          </>
        }
      >
        {error && <div className="alert alert-error">{error}</div>}
        <form id="material-produccion-form" onSubmit={guardar}>
          <section className="form-section">
            <h3>Datos del material</h3>
            <div className="form-grid">
              <Campo label="Nombre *" value={formulario.nombre} onChange={(value) => setFormulario((prev) => ({ ...prev, nombre: value }))} />
              <Campo label="Precio unitario" type="number" min="0" step="0.01" value={formulario.precio} onChange={(value) => setFormulario((prev) => ({ ...prev, precio: value }))} />
              <label className="field field-span-full">
                <span>Descripcion</span>
                <textarea rows="4" value={formulario.descripcion} onChange={(event) => setFormulario((prev) => ({ ...prev, descripcion: event.target.value }))} />
              </label>
            </div>
          </section>
        </form>
      </Modal>
    </div>
  );
}

function Campo({ label, onChange, ...props }) {
  return (
    <label className="field">
      <span>{label}</span>
      <input type="text" onChange={(event) => onChange(event.target.value)} {...props} />
    </label>
  );
}
